/**
 * The store setup checklist — the single registry of what a merchant has to do.
 *
 * Each item is declared once here and rendered by OnboardingWidget. Completion
 * is NEVER stored in this file or in component state: the backend derives every
 * `done` flag from live data (a settings row exists, a listing exists, a Xero
 * connection is enabled…) and returns it from GET /api/onboarding. This file
 * owns only the presentation contract — label, hint, and where the item sends
 * the merchant.
 *
 * Three fields carry the wiring:
 *
 *   to        the route that satisfies the item. Carries `?setup=<key>` so the
 *             destination can highlight the right field, and so the widget can
 *             reopen on the right step after an OAuth round-trip.
 *
 *   feature   the entitlement key that must be available for the item to show
 *             at all. An unentitled step is HIDDEN, not shown locked — the
 *             checklist never advertises what the store can't use. Left null
 *             where no single key cleanly governs the step; null always shows.
 *
 *   pending   true when there is genuinely nowhere to send the merchant yet.
 *             These render as "Coming soon", aren't clickable, and are EXCLUDED
 *             from the progress count — it would be dishonest to hold a store
 *             short of 100% for something the product can't do.
 *
 * Every non-pending item is backed by a real derivation in OnboardingService,
 * so it ticks itself the moment the underlying data exists.
 */

const setupLink = (path, key) => `${path}${path.includes('?') ? '&' : '?'}setup=${key}`;

const item = (key, label, hint, path, options = {}) => ({
  key,
  label,
  hint,
  to: setupLink(path, key),
  feature: options.feature || null,
  optional: options.optional === true,
  pending: options.pending === true,
});

export const CHECKLIST = [
  {
    key: 'identity',
    title: 'Your store',
    blurb: 'How your store is named and presented across the platform.',
    items: [
      item('store_name', 'Name your store', 'The name shown across the app and on your documents.', '/store-settings?field=name'),
      item('store_logos', 'Upload your logos', 'Used in the app and on every email you send.', '/store-settings?field=logos'),
      item('store_address', 'Add your trading address', 'Appears on invoices, agreements and email footers.', '/store-settings?field=physical_address'),
      item('store_url', 'Set your store web address', 'Links in your emails and PDFs point here.', '/store-settings?field=store_url'),
      item('support_whatsapp', 'Add a support WhatsApp number', 'How clients reach you from the app.', '/store-settings?field=support_whatsapp', { optional: true }),
      item('jurisdiction', 'Choose your governing law', 'Sets the legal wording on your agreements.', '/store-settings?field=jurisdiction'),
    ],
  },
  {
    key: 'documents',
    title: 'Documents and email',
    blurb: 'What your clients actually see when you send them something.',
    items: [
      item('signatory', 'Add your signatory and signature', 'Without this your agreements go out unsigned.', '/owner-admin?section=agreement-signatory'),
      item('email_sender', 'Set how your emails are signed', 'Your From name, reply-to and support address.', '/owner-admin?section=email-branding', { feature: 'branded_transactional_emails' }),
      item('email_test', 'Send yourself a test email', 'Check your logo and reply-to before a client sees them.', '/owner-admin?section=email-branding', { optional: true, feature: 'branded_transactional_emails' }),
    ],
  },
  {
    key: 'team',
    title: 'Your team',
    blurb: 'Who else can work in the store, and what they can reach.',
    items: [
      item('team_invite', 'Add your team', 'Add each person with their mobile number. They sign in with a code by SMS.', '/owner-admin?section=store-admin', { optional: true }),
      item('team_roles', 'Set everyone’s role', 'Owner, store admin or sales admin.', '/owner-admin?section=store-admin', { optional: true }),
    ],
  },
  {
    key: 'stock',
    title: 'Your Inventory',
    blurb: 'The moment your store stops being empty.',
    items: [
      // Import leads: a dealer arriving with existing inventory should meet the
      // bulk route before the one-at-a-time one.
      item('import_stock', 'Import your existing inventory', 'Bring your whole inventory in from a spreadsheet.', '/stock/import', { optional: true }),
      item('first_listing', 'Create your first listing', 'Brands, models and references are already in the catalogue — just pick one.', '/product/create/stock'),
    ],
  },
  {
    key: 'channels',
    title: 'Sales channels',
    blurb: 'Optional — connect the marketplaces you already sell on.',
    items: [
      item('channel_ebay', 'Connect eBay', 'Needs your inventory location and three business policies.', '/owner-admin?section=marketplace-accounts', { optional: true, feature: 'sync_ebay' }),
      item('channel_chrono24', 'Set up Chrono24', 'We publish a feed that Chrono24 collects from us.', '/owner-admin?section=marketplace-accounts', { optional: true, feature: 'sync_chrono24' }),
      item('channel_watchfinder', 'Connect WatchFinder', 'We’ll test the connection as soon as you add your key.', '/owner-admin?section=marketplace-accounts', { optional: true, feature: 'sync_watchfinder' }),
      item('catalog_feeds', 'Publish your catalogue feeds', 'Your own feed links for WhatsApp catalogue and Google Shopping.', '/owner-admin?section=catalog-feeds', { optional: true, feature: 'sync_whatsapp_catalog' }),
      item('channel_description', 'Write your channel description', 'Boilerplate added to every listing on a channel.', '/owner-admin?section=marketplace-accounts', { optional: true }),
    ],
  },
  {
    key: 'payments',
    title: 'Getting paid',
    blurb: 'Take card payments by link, straight into your own Stripe account.',
    items: [
      item('stripe_account', 'Connect your Stripe account', 'Stripe Link payments go straight into your own Stripe account.', '/owner-admin?section=payments', { optional: true, feature: 'stripe_payments' }),
    ],
  },
  {
    key: 'verify',
    title: 'Check it works',
    blurb: 'Two steps that prove your store is really trading.',
    items: [
      item('first_order', 'Complete one order end to end', 'Exercises your emails, your signatory and your invoice in one go.', '/requests/orders', { optional: true }),
      item('channel_check', 'Get a listing onto a channel', 'Catches a channel that connected but never published.', '/rc/listings', { optional: true }),
    ],
  },
];

/** Every item, flattened — used for counting. */
export const ALL_ITEMS = CHECKLIST.flatMap((block) => block.items);
